/**
 * Policies Hooks
 *
 * React hooks for policy management using TanStack Query
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiResponse, ApiListResponse } from '@/lib/api';
import {
    useAccessControlStore,
    type Policy,
    type AggregateCount,
} from '@/stores/access-control';
import { buildQueryString } from './query-utils';

// ============== Types ==============

export interface PolicyInput {
    name: string;
    icon?: string;
    description?: string;
    ip_access?: string | null;
    enforce_tfa?: boolean;
    admin_access?: boolean;
    app_access?: boolean;
}

// Response type for policies with aggregation
interface PolicyWithAggregates extends Omit<Policy, 'userCount' | 'roleCount'> {
    users?: AggregateCount[];
    roles?: AggregateCount[];
}

// ============== Hooks ==============

/**
 * Fetch all policies with user and role counts
 * Uses deep queries with aggregation like reference CMS
 */
export function usePolicies() {
    const { setPolicies, setPoliciesLoading } = useAccessControlStore();

    return useQuery({
        queryKey: ['policies'],
        queryFn: async () => {
            setPoliciesLoading(true);
            try {
                const queryParams = buildQueryString({
                    limit: -1,
                    fields: ['id', 'name', 'icon', 'description', 'admin_access', 'app_access', 'enforce_tfa', 'ip_access', 'users', 'roles'],
                    deep: {
                        users: {
                            _aggregate: { count: 'user' },
                        },
                        roles: {
                            _aggregate: { count: 'role' },
                        },
                    },
                    sort: 'name',
                });

                const response = await api.get<ApiListResponse<PolicyWithAggregates>>(`/policies?${queryParams}`);
                const rawPolicies = response.data || [];

                // Transform aggregation results to simple counts
                const policies: Policy[] = rawPolicies.map((policy) => ({
                    ...policy,
                    userCount: policy.users?.[0]?.count?.user ?? 0,
                    roleCount: policy.roles?.[0]?.count?.role ?? 0,
                }));

                setPolicies(policies);
                return policies;
            } finally {
                setPoliciesLoading(false);
            }
        },
    });
}

/**
 * Fetch single policy by ID
 */
export function usePolicy(id: string | undefined) {
    const { setSelectedPolicy } = useAccessControlStore();

    return useQuery({
        queryKey: ['policies', id],
        queryFn: async () => {
            if (!id) return null;
            const response = await api.get<ApiResponse<Policy>>(`/policies/${id}`);
            const policy = response.data || null;
            setSelectedPolicy(policy);
            return policy;
        },
        enabled: !!id,
    });
}

/**
 * Create a new policy
 */
export function useCreatePolicy() {
    const queryClient = useQueryClient();
    const { addPolicy } = useAccessControlStore();

    return useMutation({
        mutationFn: async (data: PolicyInput) => {
            const response = await api.post<ApiResponse<Policy>>('/policies', data);
            return response.data;
        },
        onSuccess: (policy) => {
            if (policy) addPolicy(policy);
            queryClient.invalidateQueries({ queryKey: ['policies'] });
        },
    });
}

/**
 * Update a policy
 */
export function useUpdatePolicy() {
    const queryClient = useQueryClient();
    const { updatePolicy } = useAccessControlStore();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<PolicyInput> }) => {
            const response = await api.patch<ApiResponse<Policy>>(`/policies/${id}`, data);
            return response.data;
        },
        onSuccess: (policy) => {
            if (policy) updatePolicy(policy.id, policy);
            queryClient.invalidateQueries({ queryKey: ['policies'] });
        },
    });
}

/**
 * Delete a policy
 */
export function useDeletePolicy() {
    const queryClient = useQueryClient();
    const { removePolicy } = useAccessControlStore();

    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/policies/${id}`);
            return id;
        },
        onSuccess: (id) => {
            removePolicy(id);
            queryClient.invalidateQueries({ queryKey: ['policies'] });
        },
    });
}
